import type { FilterCatalog } from '@eis/insuranceservice-sdk';
import {
  isFilterParseError,
  parseFilters,
  renderClause,
  type FilterClause,
  type FilterOperator,
} from './grammar';

/**
 * The property filter's state (SDD-017 §9) and its two pure functions: `toFilters(state)` writes
 * the `filters` value the list request sends, `fromFilters(raw, catalog)` reads one back — from a
 * URL, a shared link, the back button — into the tokens the filter shows.
 *
 * A token is one chip: `Status = Aktiv`, `Deckungsvariante = Comfort, Basic` (one IN token: several
 * values of one property are always ORed), `Fahrradwert 2000 – 5000` (one range token). The join
 * between DIFFERENT tokens is the user's choice — all of them (AND, `;`) or any of them (OR, `,`) —
 * exactly like the AWS console's property filter.
 *
 * The grammar has no parentheses and OR binds tighter than AND, so "any" over a range (which is
 * itself an AND of two bounds) is written in conjunctive normal form: `X OR (a>=1 AND a<=5)` is
 * `X,a>=1;X,a<=5`. Reading that back yields two group tokens rather than the range: equivalent,
 * not identical. Every string round-trips (`toFilters(fromFilters(s))` is `s` in normal form); every
 * state the filter itself authors round-trips token for token, except "any" with a range.
 *
 * Values are escaped by the grammar's own renderer (`;` `,` `|` `\`); `(` and `)` need no escape
 * inside a value and the parser accepts `\(` `\)` from hand-written links.
 */

/** How different tokens join: every token must match, or any one of them. */
export type JoinMode = 'all' | 'any';

/** One property, one operator, its value(s): `values` is empty for `==$null`/`!=$null`, one value otherwise, several for `=()`/`!()`. */
export interface ClauseToken {
  readonly kind: 'clause';
  readonly field: string;
  readonly op: FilterOperator;
  readonly values: readonly string[];
}

/** A from–to pair on one ordered property: `>= from` and `<= to` (either may be open). */
export interface RangeToken {
  readonly kind: 'range';
  readonly field: string;
  readonly from?: string;
  readonly to?: string;
}

/** An OR group the filter did not author (a hand-written link): shown and removable as one chip. */
export interface GroupToken {
  readonly kind: 'group';
  readonly clauses: readonly FilterClause[];
}

/** A `filters` value the grammar cannot read: kept verbatim so the server can say why, and removable. */
export interface RawToken {
  readonly kind: 'raw';
  readonly text: string;
}

export type FilterToken = ClauseToken | RangeToken | GroupToken | RawToken;

export interface FilterState {
  readonly tokens: readonly FilterToken[];
  readonly join: JoinMode;
}

export const EMPTY_FILTER_STATE: FilterState = { tokens: [], join: 'all' };

/** Operators that carry no value. */
export const NULL_OPERATORS: readonly FilterOperator[] = ['==$null', '!=$null'];
/** Operators that carry a list. */
export const LIST_OPERATORS: readonly FilterOperator[] = ['=()', '!()'];

const ORDERED_TYPES = new Set(['NUMBER', 'INTEGER', 'MONEY', 'DATE', 'DATETIME']);

/** A token as its clauses: AND over OR groups (conjunctive normal form). */
function cnfOf(token: FilterToken): FilterClause[][] | string {
  switch (token.kind) {
    case 'raw':
      return token.text;
    case 'group':
      return [[...token.clauses]];
    case 'range': {
      const groups: FilterClause[][] = [];
      if (token.from !== undefined && token.from !== '') {
        groups.push([{ field: token.field, op: '>=', value: token.from }]);
      }
      if (token.to !== undefined && token.to !== '') {
        groups.push([{ field: token.field, op: '<=', value: token.to }]);
      }
      return groups;
    }
    case 'clause':
      return [[clauseOf(token)]];
  }
}

function clauseOf(token: ClauseToken): FilterClause {
  if (token.op === '==$null' || token.op === '!=$null') return { field: token.field, op: token.op };
  if (token.op === '=()' || token.op === '!()') {
    return { field: token.field, op: token.op, values: token.values };
  }
  return { field: token.field, op: token.op, value: token.values[0] ?? '' };
}

const renderGroups = (groups: readonly (readonly FilterClause[])[]): string =>
  groups.map((group) => group.map(renderClause).join(',')).join(';');

/** OR of two CNFs, distributed: (∧Ai) ∨ (∧Bj) = ∧(Ai ∨ Bj). */
function orCnf(a: FilterClause[][], b: FilterClause[][]): FilterClause[][] {
  if (a.length === 0) return b;
  if (b.length === 0) return a;
  return a.flatMap((left) => b.map((right) => [...left, ...right]));
}

/** The `filters` value a state means — empty when there is nothing to filter. */
export function toFilters(state: FilterState): string {
  const raws: string[] = [];
  const cnfs: FilterClause[][][] = [];
  for (const token of state.tokens) {
    const cnf = cnfOf(token);
    if (typeof cnf === 'string') raws.push(cnf);
    else if (cnf.length > 0) cnfs.push(cnf);
  }
  const combined = state.join === 'any' ? cnfs.reduce<FilterClause[][]>(orCnf, []) : cnfs.flat();
  return [...raws, renderGroups(combined)].filter((part) => part !== '').join(';');
}

function tokenOf(clause: FilterClause): ClauseToken {
  if ('values' in clause) {
    return { kind: 'clause', field: clause.field, op: clause.op, values: [...clause.values] };
  }
  if ('value' in clause) {
    return { kind: 'clause', field: clause.field, op: clause.op, values: [clause.value] };
  }
  return { kind: 'clause', field: clause.field, op: clause.op, values: [] };
}

function typeOf(catalog: FilterCatalog | undefined, key: string): string | undefined {
  return catalog?.fields?.find((field) => field.key === key)?.type;
}

/** Folds `>=` immediately followed by `<=` on one ordered property into one range token. */
function mergeRanges(tokens: FilterToken[], catalog: FilterCatalog | undefined): FilterToken[] {
  const out: FilterToken[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const current = tokens[i];
    const next = tokens[i + 1];
    if (
      current?.kind === 'clause' &&
      next?.kind === 'clause' &&
      current.op === '>=' &&
      next.op === '<=' &&
      current.field === next.field
    ) {
      const type = typeOf(catalog, current.field);
      if (catalog === undefined || (type !== undefined && ORDERED_TYPES.has(type))) {
        out.push({
          kind: 'range',
          field: current.field,
          from: current.values[0],
          to: next.values[0],
        });
        i++;
        continue;
      }
    }
    if (current !== undefined) out.push(current);
  }
  return out;
}

/**
 * Reads a `filters` value into the tokens a property filter shows. A blank value is no token; an
 * unreadable one is ONE raw token (never "no filter": the request still carries it and the server
 * says why). `catalog`, when given, decides which `>=`/`<=` pairs are ranges (ordered types only).
 */
export function fromFilters(raw: string | undefined, catalog?: FilterCatalog): FilterState {
  const text = (raw ?? '').trim();
  if (text === '') return EMPTY_FILTER_STATE;
  let groups;
  try {
    groups = parseFilters(text).and;
  } catch (error) {
    if (isFilterParseError(error)) return { tokens: [{ kind: 'raw', text }], join: 'all' };
    throw error;
  }
  const [only] = groups;
  if (groups.length === 1 && only !== undefined && only.or.length > 1) {
    return { tokens: only.or.map(tokenOf), join: 'any' };
  }
  const tokens: FilterToken[] = groups.map((group) =>
    group.or.length === 1 && group.or[0] !== undefined
      ? tokenOf(group.or[0])
      : { kind: 'group', clauses: [...group.or] },
  );
  return { tokens: mergeRanges(tokens, catalog), join: 'all' };
}

const POSITIVE_LIST: Readonly<Partial<Record<FilterOperator, FilterOperator>>> = {
  '==': '=()',
  '=()': '=()',
  '!=': '!()',
  '!()': '!()',
};

/**
 * Adds one value to the state the way the filter means it: a second value of the same property
 * with the same kind of equality joins the existing token as IN / NOT IN (`= Comfort` + `= Basic`
 * → `= (Comfort|Basic)`); a range replaces the property's range; anything else is a new token.
 */
export function addToken(state: FilterState, token: ClauseToken | RangeToken): FilterState {
  if (token.kind === 'range') {
    const kept = state.tokens.filter((t) => !(t.kind === 'range' && t.field === token.field));
    return { ...state, tokens: [...kept, token] };
  }
  const listOp = POSITIVE_LIST[token.op];
  if (listOp !== undefined) {
    const index = state.tokens.findIndex(
      (t) => t.kind === 'clause' && t.field === token.field && POSITIVE_LIST[t.op] === listOp,
    );
    const existing = state.tokens[index];
    if (existing?.kind === 'clause') {
      const values = [...new Set([...existing.values, ...token.values])];
      const merged: ClauseToken = {
        kind: 'clause',
        field: token.field,
        op: values.length > 1 ? listOp : existing.op,
        values,
      };
      return { ...state, tokens: state.tokens.map((t, i) => (i === index ? merged : t)) };
    }
  }
  return { ...state, tokens: [...state.tokens, token] };
}

/** The state without the token at `index`. */
export function removeToken(state: FilterState, index: number): FilterState {
  return { ...state, tokens: state.tokens.filter((_, i) => i !== index) };
}

/**
 * ANDs `filters` values: a screen's own scope (`tarifSlug==fahrrad`, a quick filter) with the
 * user's. `;` binds weakest, so `a==1;b==2,b==3` stays "a AND (b OR b)" whatever the user's join.
 * Blank parts are dropped; nothing at all is `undefined`, so the request carries no `filters`.
 */
export function andFilters(...parts: readonly (string | undefined)[]): string | undefined {
  const kept = parts.filter((part): part is string => part !== undefined && part.trim() !== '');
  return kept.length === 0 ? undefined : kept.join(';');
}
